import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../actions/api";
import { hideLoader, showLoader } from "./loaderSlice";

export const getOperationTheatreList = createAsyncThunk(
  "getOperationTheatreList",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/getall/clinic/facilities`
      );

      if (res.status === 200) {
        const theatres = res.data.data.filter(
          (p) => p.type === "OperationTheatre"
        );
        thunkAPI.dispatch(hideLoader());
        return theatres;
      }
      thunkAPI.dispatch(hideLoader());
      return [];
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch operation theatres");
    }
  }
);

export const createOperationTheatre = createAsyncThunk(
  "createOperationTheatre",
  async ({ name }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/create/clinic/facilites?type=OperationTheatre&name=${name}&status=true`
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to create operation theatre");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const updateOperationTheatre = createAsyncThunk(
  "updateOperationTheatre",
  async ({ facilityId, name, status = true }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/update/clinic/facilites/${facilityId}?type=OperationTheatre&name=${name}&status=${status}`
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to update operation theatre");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const deleteOperationTheatre = createAsyncThunk(
  "deleteOperationTheatre",
  async ({ facilityId }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).delete(
        `/delete/clinic/facilites/${facilityId}`
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return facilityId; // Return ID to remove from list
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to delete operation theatre");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

const operationTheatreSlice = createSlice({
  name: "operationTheatre",
  initialState: {
    list: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getOperationTheatreList.fulfilled, (state, action) => {
        state.list = action.payload ?? [];
        state.loading = false;
        state.error = null;
      })
      .addCase(createOperationTheatre.fulfilled, (state, action) => {
        state.list.push(action.payload);
        state.loading = false;
        state.error = null;
      })
      .addCase(updateOperationTheatre.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.list.findIndex(
          (p) => p.facilityId === updated.facilityId
        );
        if (index !== -1) {
          state.list[index] = updated;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteOperationTheatre.fulfilled, (state, action) => {
        const id = action.payload;
        state.list = state.list.filter((p) => p.facilityId !== id);
        state.loading = false;
        state.error = null;
      })
      .addCase(getOperationTheatreList.pending, (state) => {
        state.loading = true;
      })
      .addCase(createOperationTheatre.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateOperationTheatre.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteOperationTheatre.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOperationTheatreList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching operation theatres.";
      });
  },
});

export default operationTheatreSlice.reducer;
